/**
 * Collapsible Accordion Script
 * Converts richtext content with H3 headings into collapsible accordion sections
 * The H3 becomes the clickable title, and following content becomes the collapsible body
 */

(function() {
  'use strict';

  // Initialize accordion when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccordion);
  } else {
    initAccordion();
  }

  function initAccordion() {
    // Find all richtext containers that should have accordion functionality
    const richtextContainers = document.querySelectorAll('.richtext-accordion, [data-accordion="true"]');
    
    richtextContainers.forEach(container => {
      convertToAccordion(container);
    });
  }

  function convertToAccordion(container) {
    // Find all H3 elements in the container
    const h3Elements = container.querySelectorAll('h3');
    
    if (h3Elements.length === 0) return;

    // Create accordion structure
    const accordionWrapper = document.createElement('div');
    accordionWrapper.className = 'accordion-wrapper';
    
    h3Elements.forEach((h3, index) => {
      // Create accordion item
      const accordionItem = document.createElement('div');
      accordionItem.className = 'accordion-item';
      
      // Create accordion header (button)
      const accordionHeader = document.createElement('button');
      accordionHeader.className = 'accordion-header';
      accordionHeader.setAttribute('aria-expanded', 'false');
      accordionHeader.setAttribute('aria-controls', `accordion-content-${index}`);
      accordionHeader.innerHTML = `
        ${h3.innerHTML}
        <span class="accordion-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      `;
      
      // Create accordion content panel
      const accordionContent = document.createElement('div');
      accordionContent.className = 'accordion-content';
      accordionContent.id = `accordion-content-${index}`;
      accordionContent.setAttribute('aria-hidden', 'true');
      
      // Create inner wrapper for content
      const accordionInner = document.createElement('div');
      accordionInner.className = 'accordion-content-inner';
      
      // Collect all content until the next H3
      let currentElement = h3.nextElementSibling;
      const contentElements = [];
      
      while (currentElement && currentElement.tagName !== 'H3') {
        contentElements.push(currentElement);
        currentElement = currentElement.nextElementSibling;
      }
      
      // Move content into accordion
      contentElements.forEach(el => {
        accordionInner.appendChild(el.cloneNode(true));
      });
      
      accordionContent.appendChild(accordionInner);
      
      // Add click event to toggle
      accordionHeader.addEventListener('click', function() {
        toggleAccordion(accordionItem, accordionHeader, accordionContent);
      });
      
      // Assemble accordion item
      accordionItem.appendChild(accordionHeader);
      accordionItem.appendChild(accordionContent);
      accordionWrapper.appendChild(accordionItem);
      
      // Remove original elements
      h3.style.display = 'none';
      contentElements.forEach(el => {
        el.style.display = 'none';
      });
    });
    
    // Insert accordion into container
    container.appendChild(accordionWrapper);
  }

  function toggleAccordion(item, header, content) {
    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      // Close
      header.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');
      item.classList.remove('active');
      content.style.maxHeight = '0px';
    } else {
      // Open
      header.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      item.classList.add('active');
      
      // Calculate and set max-height for smooth animation
      const inner = content.querySelector('.accordion-content-inner');
      content.style.maxHeight = inner.scrollHeight + 'px';
    }
  }

  // Add CSS styles
  const style = document.createElement('style');
  style.textContent = `
    .accordion-wrapper {
      margin: 20px 0;
    }

    .accordion-item {
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      margin-bottom: 12px;
      overflow: hidden;
      transition: all 0.3s ease;
    }

    .accordion-item:hover {
      border-color: #ccc;
    }

    .accordion-item.active {
      border-color: #999;
    }

    .accordion-header {
      width: 100%;
      padding: 16px 20px;
      background: #f8f9fa;
      border: none;
      text-align: left;
      font-size: 1.1rem;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      transition: background 0.3s ease;
      font-family: inherit;
      color: inherit;
    }

    .accordion-header:hover {
      background: #e9ecef;
    }

    .accordion-item.active .accordion-header {
      background: #e9ecef;
    }

    .accordion-icon {
      display: flex;
      align-items: center;
      transition: transform 0.3s ease;
      flex-shrink: 0;
      margin-left: 12px;
    }

    .accordion-item.active .accordion-icon {
      transform: rotate(180deg);
    }

    .accordion-content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease;
    }

    .accordion-content-inner {
      padding: 20px;
    }

    .accordion-content-inner p {
      margin: 0 0 12px 0;
      line-height: 1.6;
    }

    .accordion-content-inner p:last-child {
      margin-bottom: 0;
    }

    .accordion-content-inner ul,
    .accordion-content-inner ol {
      margin: 0 0 12px 0;
      padding-left: 24px;
    }

    .accordion-content-inner li {
      margin-bottom: 8px;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .accordion-header {
        padding: 14px 16px;
        font-size: 1rem;
      }

      .accordion-content-inner {
        padding: 16px;
      }
    }
  `;
  
  document.head.appendChild(style);

  // Support for Shopify theme editor
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', function() {
      setTimeout(initAccordion, 100);
    });
  }
})();
